/**
 * Component for handling header spacing globally
 * Responsible for keeping CSS variables updated when heights change
 */

class HeaderSpacingManager {
  constructor() {
    this.isInitialized = false
    this.resizeObserver = null
    this.mutationObserver = null
    this.updateTimeout = null
  }

  init() {
    if (this.isInitialized) return

    this.isInitialized = true

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupObservers()
        this.updateSpacing()
      })
    } else {
      this.setupObservers()
      this.updateSpacing()
    }
  }

  setupObservers() {
    // Observer for header and announcement bar size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.debouncedUpdate()
    })

    // Observer for DOM changes (new elements, class changes, etc.)
    this.mutationObserver = new MutationObserver(() => {
      this.debouncedUpdate()
    })

    // Observe the header and announcement bar
    const header = document.querySelector('hh-header')
    const announcement = document.querySelector('#announcement-bar')

    if (header) {
      this.resizeObserver.observe(header)
    }

    if (announcement) {
      this.resizeObserver.observe(announcement)
    }

    // Observe changes in the body (for class changes that affect the header)
    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    })

    // Observe changes in the viewport
    window.addEventListener('resize', () => {
      this.debouncedUpdate()
    })

    // Observe orientation changes on mobile devices
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.updateSpacing()
      }, 100)
    })
  }

  debouncedUpdate() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
    }

    this.updateTimeout = setTimeout(() => {
      this.updateSpacing()
    }, 100)
  }

  updateSpacing() {
    if (window.headerSpacingUtils) {
      window.headerSpacingUtils.updateHeaderSpacing()
    }
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
    }

    this.isInitialized = false
  }
}

// Create global instance
window.headerSpacingManager = new HeaderSpacingManager()

// Initialize automatically
window.headerSpacingManager.init()

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeaderSpacingManager
}
